/* Moving a focus ring around a hierarchy, with nothing in it that knows about a rectangle, a
   circle or an arc. All three views pass the same `toDrawnOrder` list. */

import { isTruthy } from '@ardoq/common-helpers';

export type NavigableNode = {
  id: string;
  /** The component the node stands for, or `null` for a group a rule built rather than a component. */
  modelId: string | null;
  label: string | null;
  /** 1-based, which is what `aria-level` wants. `d3` counts the undrawn root as 0. */
  level: number;
  parentId: string | null;
};

/**
 * Where a node sits among its siblings, counted over the *drawn* list: the depth setting can
 * cut a level, and a count from the untrimmed tree would announce "3 of 7" about a level of
 * three.
 */
export type NodePosition = {
  setSize: number;
  positionInSet: number;
};

/** The shape a rectangle, a circle and an arc have in common. */
type LaidOutNode = {
  depth: number;
  data: { id: string; modelId: string | null; label: string | null };
  parent: { data: { id: string } } | null;
};

/**
 * The drawn nodes as something to navigate, in the order they were drawn in.
 *
 * `parentId` is `null` where the parent is not in the list, not merely where `d3` has none: the
 * undrawn root is every top-level node's parent.
 */
export const toNavigableNodes = (
  descendants: readonly LaidOutNode[]
): NavigableNode[] => {
  const drawn = new Set(descendants.map(({ data }) => data.id));
  return descendants.map(({ depth, data, parent }) => ({
    id: data.id,
    modelId: data.modelId,
    label: data.label,
    level: depth,
    parentId: parent && drawn.has(parent.data.id) ? parent.data.id : null,
  }));
};

/**
 * The nodes that have a child on screen, read off the same `parentId` ArrowRight reads.
 */
export const toParentIds = (
  nodes: readonly NavigableNode[]
): ReadonlySet<string> =>
  new Set(nodes.map(({ parentId }) => parentId).filter(isTruthy));

export const toNodePositions = (
  nodes: readonly NavigableNode[]
): Map<string, NodePosition> => {
  /* O(n). A flat level is an ordinary shape here - a workspace-rule group puts every component
     under one parent - so a spread per insert plus an `indexOf` per node is quadratic. */
  const siblingsByParent = new Map<string | null, string[]>();
  for (const { id, parentId } of nodes) {
    const siblings = siblingsByParent.get(parentId);
    if (siblings) {
      siblings.push(id);
      continue;
    }
    siblingsByParent.set(parentId, [id]);
  }

  const positions = new Map<string, NodePosition>();
  for (const siblings of siblingsByParent.values()) {
    siblings.forEach((id, index) =>
      positions.set(id, {
        setSize: siblings.length,
        positionInSet: index + 1,
      })
    );
  }
  return positions;
};

/**
 * What a node is called, to something that cannot see it: the label and the value together,
 * since the area a reader cannot see *is* the value.
 */
export const toAccessibleName = (
  label: string | null,
  valueLabel: string
): string => (label ? `${label} (${valueLabel})` : valueLabel);

export type HierarchyNavigationKey =
  | 'ArrowDown'
  | 'ArrowUp'
  | 'ArrowRight'
  | 'ArrowLeft'
  | 'Home'
  | 'End';

const NAVIGATION_KEYS: ReadonlySet<string> = new Set([
  'ArrowDown',
  'ArrowUp',
  'ArrowRight',
  'ArrowLeft',
  'Home',
  'End',
]);

export const isHierarchyNavigationKey = (
  key: string
): key is HierarchyNavigationKey => NAVIGATION_KEYS.has(key);

/**
 * Where a key takes the focus, or `null` where it takes it nowhere.
 *
 * Down and up walk the drawn order; right and left move into the first child and out to the
 * parent. Nothing is collapsible, so an edge answers `null` rather than wrapping.
 */
export const toFocusedIdAfterKey = (
  nodes: readonly NavigableNode[],
  focusedId: string,
  key: HierarchyNavigationKey
): string | null => {
  if (!nodes.length) return null;
  const index = nodes.findIndex(({ id }) => id === focusedId);
  if (index === -1) return nodes[0].id;

  if (key === 'Home') return nodes[0].id;
  if (key === 'End') return nodes[nodes.length - 1].id;
  if (key === 'ArrowDown') return nodes[index + 1]?.id ?? null;
  if (key === 'ArrowUp') return nodes[index - 1]?.id ?? null;
  if (key === 'ArrowLeft') return nodes[index].parentId;
  /* Searched, not read off the next position: pre-order makes those agree today, and this stays
     right if the drawn order changes. */
  return nodes.find(({ parentId }) => parentId === focusedId)?.id ?? null;
};
